import {Typography, Select, Button, Tooltip, Checkbox} from 'antd';
import {useState} from 'react';
const {Text} = Typography;

// Add styles to prevent layout changes on hover
const productCardStyles = `
  .product-card:hover {
    display: flex !important;
    flex-direction: row !important;
    align-items: flex-start !important;
    width: 100% !important;
    gap: 12px !important;
  }
  .product-card * {
    transition: none !important;
  }

  /* The sidebar can be dragged narrow. Below these widths the fixed 80px thumb
     and the two side-by-side actions stop fitting, so the thumb shrinks first
     and the actions stack rather than squeezing into unreadable stubs.
     !important is needed to beat the inline styles on these nodes. */
  @media (max-width: 380px) {
    .product-card {
      gap: 8px !important;
    }
    .product-card__media {
      width: 60px !important;
      height: 60px !important;
      min-width: 60px !important;
      max-width: 60px !important;
      min-height: 60px !important;
      max-height: 60px !important;
    }
    .product-card__body {
      width: calc(100% - 68px) !important;
    }
  }

  /* Variant tags have to be capped, or one long title cannot fit and antd's
     responsive mode collapses EVERY tag into a bare "+ 2 ...", leaving the agent
     with no idea which variants are selected. Capped and ellipsised, the first
     titles stay readable and only the genuine overflow is summarised. */
  .ss-variant-select .ant-select-selection-overflow-item
    .ant-select-selection-item {
    max-width: 130px;
  }
  .ss-variant-select .ant-select-selection-item-content {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ss-variant-select-more {
    color: #0954A5;
    font-weight: 600;
    font-size: 12px;
    cursor: default;
  }

  @media (max-width: 380px) {
    .ss-variant-select .ant-select-selection-overflow-item
      .ant-select-selection-item {
      max-width: 96px;
    }
  }

  @media (max-width: 320px) {
    .product-card__actions {
      flex-wrap: wrap !important;
    }
    .product-card__actions > * {
      width: 100% !important;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = productCardStyles;
  document.head.appendChild(styleSheet);
}
const ProductSection = ({
  products,
  updateCartItems,
  updateCartItemsMulti,
  cartItems,
  updateCartVariant,
  sendProductInChat,
  isSendInChatDisabled,
  showCheckboxes = false,
  onCheckboxChange = () => {},
  checkedVariantIds = [],
  currencySymbol,
  // Which section this list is rendered in. Stored on the cart item so a section
  // can show its cart badge without having to load its products first.
  collectionId = null,
}) => {
  const productItems = (products || [])
    .filter(p => Array.isArray(p.variants) && p.variants.length > 0)
    .map(product => (
      <ProductCard
        key={`${product.productId}`}
        product={product}
        updateCartItems={updateCartItems}
        updateCartItemsMulti={updateCartItemsMulti}
        cartItems={cartItems}
        updateCartVariant={updateCartVariant}
        sendProductInChat={sendProductInChat}
        isSendInChatDisabled={isSendInChatDisabled}
        showCheckboxes={showCheckboxes}
        onCheckboxChange={onCheckboxChange}
        checkedVariantIds={checkedVariantIds}
        currencySymbol={currencySymbol}
        collectionId={collectionId}
      />
    ));
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '10px',
      }}
    >
      {productItems}
    </div>
  );
};

const ProductCard = ({
  product,
  updateCartItems,
  updateCartItemsMulti,
  cartItems,
  updateCartVariant,
  sendProductInChat,
  isSendInChatDisabled,
  showCheckboxes = false,
  onCheckboxChange = () => {},
  checkedVariantIds = [],
  currencySymbol,
  collectionId = null,
}) => {
  // Seed from what is already selected or in the cart, not just the first variant:
  // this card remounts whenever the agent comes back from the cart screen, and the
  // ticks and quantities have to come back with it.
  const getInitialSelectedVariantIds = () => {
    const variantIds = (product.variants || []).map(variant => variant.id);
    const checked = variantIds.filter(id => checkedVariantIds.includes(id));
    if (checked.length) {
      return checked;
    }
    const inCart = variantIds.filter(id =>
      cartItems.some(item => item.variant_id === id)
    );
    if (inCart.length) {
      return inCart;
    }
    return variantIds.length ? [variantIds[0]] : [];
  };
  const [selectedVariantIds, setSelectedVariantIds] = useState(
    getInitialSelectedVariantIds
  );
  const [isSending, setIsSending] = useState(false);
  // Multi-variant selection only makes sense where a "Send Selected" flow exists.
  // The product-wise view renders without checkboxes and stays single-select.
  const isMultiSelect = showCheckboxes;
  // The cart still tracks one variant per card; the first selected one is primary.
  const primaryVariantId = selectedVariantIds[0] || null;
  const cartItem = cartItems.find(item => item.variant_id === primaryVariantId);
  const quantity = cartItem ? cartItem?.quantity : 0;
  const options = product.variants.map(variant => ({
    value: variant.id,
    label: variant.title,
  }));
  const variantSelected = product.variants.find(
    variant => variant.id === primaryVariantId
  );
  // The checkbox covers every variant chosen on this card, so it can be partial.
  const checkedCount = selectedVariantIds.filter(id =>
    checkedVariantIds.includes(id)
  ).length;
  const isChecked =
    selectedVariantIds.length > 0 && checkedCount === selectedVariantIds.length;
  const isIndeterminate =
    checkedCount > 0 && checkedCount < selectedVariantIds.length;

  // Keep the parent selection in step with the dropdown: variants removed here are
  // always dropped from it, so a deselected variant can never be sent invisibly.
  const handleVariantChange = value => {
    const next = Array.isArray(value) ? value : [value];
    if (!next.length) {
      return;
    }
    const removed = selectedVariantIds.filter(id => !next.includes(id));
    const added = next.filter(id => !selectedVariantIds.includes(id));
    setSelectedVariantIds(next);
    if (removed.length) {
      onCheckboxChange(removed, false);
    }
    if (added.length && isChecked) {
      onCheckboxChange(added, true);
    }
  };

  // Cart actions apply to EVERY variant chosen on the card, in one state update.
  const cartEntries = selectedVariantIds.map(variantId => {
    const variant = product.variants.find(v => v.id === variantId);
    return {
      variantId,
      productId: product.productId,
      collectionId,
      productContext: {
        productTitle: product.title,
        variantTitle: variant?.title,
        price: variant?.price,
        sku: variant?.sku,
      },
    };
  });

  const setCartQuantity = nextQuantity => {
    if (updateCartItemsMulti) {
      updateCartItemsMulti(cartEntries, nextQuantity);
      return;
    }
    // Fallback for callers that never passed the bulk updater.
    updateCartItems(primaryVariantId, product.productId, nextQuantity, {
      productTitle: product.title,
      variantTitle: variantSelected?.title,
      price: variantSelected?.price,
      sku: variantSelected?.sku,
    });
  };

  const handleSendInChat = async () => {
    if (!selectedVariantIds.length || isSending) {
      return;
    }
    setIsSending(true);
    try {
      // One call for every chosen variant, so the agent gets a single toast back.
      await sendProductInChat(selectedVariantIds);
    } finally {
      setIsSending(false);
    }
  };
  return (
    <div
      className="product-card"
      style={{
        alignItems: 'flex-start',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: '12px',
        marginBottom: '16px',
        transition: 'none',
        boxSizing: 'border-box',
      }}
    >
      {showCheckboxes && (
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          // Toggle on what is actually selected, not on `e.target.checked`: antd
          // reports a click on an indeterminate box as "check", so a card left
          // partly selected by the 30-product limit could never be cleared.
          onChange={() => onCheckboxChange(selectedVariantIds, checkedCount === 0)}
          style={{marginRight: 8, marginTop: 4}}
        />
      )}
      <div
        className="product-card__media"
        style={{
          borderRadius: '8px',
          width: '80px',
          height: '80px',
          overflow: 'hidden',
          flexShrink: 0,
          minWidth: '80px',
          maxWidth: '80px',
          minHeight: '80px',
          maxHeight: '80px',
          transition: 'none',
        }}
      >
        <img
          src={
            product.image ||
            'https://t4.ftcdn.net/jpg/14/50/59/67/360_F_1450596725_BIFfrrI1UWkxA7q0znXHupgKmwtcXkiQ.jpg'
          }
          alt={product.title}
          style={{width: '100%', height: '100%', objectFit: 'cover'}}
        />
      </div>
      <div
        className="product-card__body"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: '8px',
          minWidth: 0,
          width: 'calc(100% - 92px)',
          transition: 'none',
        }}
      >
        <Tooltip title={product.title}>
          <Text
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#26292B',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {product.title}
          </Text>
        </Tooltip>
        {variantSelected?.sku && (
          <Text
            style={{
              fontSize: '12px',
              color: '#6B7280',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            SKU: {variantSelected.sku}
          </Text>
        )}
        <Text
          style={{
            fontSize: '14px',
            color: '#26292B',
            fontWeight: 500,
          }}
        >
          {currencySymbol}
          {variantSelected?.price}
        </Text>
        {product.variants.length > 1 && (
          <Select
            className="ss-variant-select"
            style={
              isMultiSelect
                ? {width: '100%', minHeight: '35px'}
                : {width: '100%', height: '35px'}
            }
            mode={isMultiSelect ? 'multiple' : undefined}
            maxTagCount="responsive"
            // Names the overflow instead of antd's bare "+ 2 ...", and puts the
            // hidden titles one hover away.
            maxTagPlaceholder={omitted => (
              <Tooltip
                title={omitted.map(item => item.label).join(', ')}
                placement="top"
              >
                <span className="ss-variant-select-more">
                  +{omitted.length} more
                </span>
              </Tooltip>
            )}
            value={isMultiSelect ? selectedVariantIds : primaryVariantId}
            onChange={handleVariantChange}
            options={options}
          />
        )}
        <div
          className="product-card__actions"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            width: '100%',
            gap: '8px',
          }}
        >
          {/* Hide Send in Chat button if sendProductInChat is null (collections mode) */}
          {sendProductInChat && (
            <Button
              onClick={handleSendInChat}
              disabled={isSendInChatDisabled || isSending}
              style={{
                backgroundColor: isSendInChatDisabled ? '#E5E5E5' : '#F1F3F5',
                color: isSendInChatDisabled ? '#999999' : '#26292B',
                border: 'none',
                width: '48%',
                fontWeight: 600,
                cursor: isSendInChatDisabled ? 'not-allowed' : 'pointer',
              }}
            >
              Send in Chat
            </Button>
          )}
          {cartItem?.quantity > 0 ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '5px',
                overflow: 'hidden',
                width: sendProductInChat ? '48%' : '100%',
              }}
            >
              <Button
                onClick={() => setCartQuantity(quantity - 1)}
                style={{
                  backgroundColor: '#EDF6FF',
                  width: '33%',
                  height: '28px',
                  borderRadius: 0,
                  border: 'none',
                  fontWeight: 600,
                }}
              >
                <img
                  width={14}
                  height={14}
                  src={'/minusIcon.svg'}
                  alt="minus"
                />
              </Button>
              <div
                style={{
                  background: '#369eff',
                  width: '33%',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '12px',
                  fontWeight: '600',
                  lineHeight: '16px',
                }}
              >
                {quantity}
              </div>
              <Button
                onClick={() => setCartQuantity(quantity + 1)}
                style={{
                  backgroundColor: '#EDF6FF',
                  width: '33%',
                  height: '28px',
                  borderRadius: 0,
                  border: 'none',
                }}
              >
                <img width={14} height={14} src={'/plusIcon.svg'} alt="plus" />
              </Button>
            </div>
          ) : (
            <Button
              style={{
                backgroundColor: '#EDF6FF',
                color: '#0954A5',
                border: 'none',
                width: sendProductInChat ? '48%' : '100%',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '5px',
              }}
              onClick={() => setCartQuantity(1)}
            >
              <img width={14} height={14} src={'/plusIcon.svg'} alt="plus" />
              Add to Cart
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSection;
